import { CheckIcon } from "lucide-react-native";
import React, { memo } from "react";
import { Platform } from "react-native";

import { Box } from "@/components/atoms/Box";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";
import { ifWeb } from "@/utils/platform";

/* design-system: dynamic-style waiver — data-driven value (measured container width for responsive text truncation) */
const menuItemWidthStyle = (widthName: number) =>
  ({
    maxWidth:
      Platform.OS === "web"
        ? ifWeb(widthName * 0.14, widthName * 0.17)
        : widthName * 0.6,
  }) as const;

/**
 * Props for WatchlistMenuItemView component
 */
export interface WatchlistMenuItemViewProps {
  readonly name: string;
  readonly isSelected: boolean;
  readonly isFirst: boolean;
  readonly widthName: number;
  readonly onPress: () => void;
}

/**
 * View component for individual watchlist menu item
 * @param root0 - Component props
 * @param root0.name - Watchlist name to display
 * @param root0.isSelected - Whether this item is currently selected
 * @param root0.isFirst - Whether this is the first item (affects border styling)
 * @param root0.widthName - Window width for responsive text truncation
 * @param root0.onPress - Callback invoked when item is pressed
 */
const WatchlistMenuItemView = ({
  name,
  isSelected,
  isFirst,
  widthName,
  onPress,
}: WatchlistMenuItemViewProps) => (
  <Pressable onPress={onPress}>
    <Stack
      direction="horizontal"
      className={`items-center ${ifWeb("gap-5 px-3", "gap-3 px-4")} py-3 ${!isFirst ? "border-t border-surface-muted" : ""}`}
    >
      <Box className="w-4">{isSelected ? <Icon as={CheckIcon} /> : null}</Box>
      <Box
        className="flex-1 overflow-hidden"
        // eslint-disable-next-line no-restricted-syntax -- runtime-measured popover container width; continuous value not expressible as a closed sizing token
        UNSAFE_style={menuItemWidthStyle(widthName)}
      >
        {/* SE-6773: the web arm used to name an `md` size step, for which this
            theme has no fontSize token — so it compiled to nothing and the
            element rendered at the Text atom's own default (size "md" ->
            text-title-sm). Naming that default keeps the render identical. */}
        <Text
          variant="unstyled"
          className={`truncate font-medium ${ifWeb("text-title-sm", "text-title")}`}
        >
          {name}
        </Text>
      </Box>
      <Box className="w-6" />
    </Stack>
  </Pressable>
);

WatchlistMenuItemView.displayName = "WatchlistMenuItemView";

export default memo(WatchlistMenuItemView);
